import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  CircularProgress,
  LinearProgress,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Send as SendIcon, 
  Build as ToolIcon, 
  CheckCircle as CompletedIcon,
  ExpandMore as ExpandMoreIcon,
  PlayCircleOutline as StartIcon
} from '@mui/icons-material';
import { getChat, sendStreamingMessage } from '../../services/chatService';
import { Chat, Message, MessageRole, ResearchStatus, ToolResult } from '../../types/chat';
import { formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import styled from 'styled-components';

// Utility function to detect if a response is a blocking/access restriction message
const isBlockingOrAccessMessage = (content: string, message?: string): boolean => {
  const textToCheck = (content + ' ' + (message || '')).toLowerCase();
  
  const blockingIndicators = [
    'website access blocked',
    'access restricted', 
    'blocking automated access',
    'analysis blocked',
    'cloudflare',
    'security protection',
    'unable to access',
    'blocking detected',
    'alternative assistance available',
    'website blocking detected',
    'dealership is blocking',
    'try searching for this vehicle on',
    'provide vehicle details manually'
  ];
  
  return blockingIndicators.some(indicator => textToCheck.includes(indicator));
};

// Create a styled div to wrap around ReactMarkdown
const MarkdownContainer = styled.div`
  font-family: inherit;
  color: inherit;
  font-size: 0.95rem;
  line-height: 1.6;

  /* Remove trailing margin from last element */
  > *:last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0 0 0.75em;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul, ol {
    margin: 0.25em 0 0.75em 1.5em;
    padding: 0;
  }

  li {
    margin-bottom: 0.25em;
  }

  a {
    color: #818cf8;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  strong, b {
    font-weight: 700;
  }

  em, i {
    font-style: italic;
  }

  h1, h2, h3, h4, h5, h6 {
    color: inherit;
    font-weight: 600;
    margin: 1em 0 0.4em;
    line-height: 1.3;
  }

  h1 { font-size: 1.2em; }
  h2 { font-size: 1.1em; }
  h3 { font-size: 1.05em; }
  h4, h5, h6 { font-size: 1em; }

  code {
    font-family: 'Roboto Mono', 'Courier New', monospace;
    background-color: rgba(255, 255, 255, 0.12);
    padding: 0.15em 0.4em;
    border-radius: 3px;
    font-size: 0.875em;
    word-break: break-all;
  }

  pre {
    background-color: rgba(0, 0, 0, 0.4);
    padding: 0.75em 1em;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0.75em 0;

    code {
      background-color: transparent;
      padding: 0;
      font-size: 0.875em;
      word-break: normal;
    }
  }

  blockquote {
    border-left: 3px solid #818cf8;
    padding-left: 0.75em;
    margin: 0.5em 0;
    opacity: 0.85;
    font-style: italic;
  }

  hr {
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    margin: 0.75em 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.75em 0;
    font-size: 0.9em;

    th, td {
      border: 1px solid rgba(255, 255, 255, 0.15);
      padding: 0.4em 0.6em;
      text-align: left;
    }

    th {
      background-color: rgba(255, 255, 255, 0.08);
      font-weight: 600;
    }

    tr:nth-child(even) {
      background-color: rgba(255, 255, 255, 0.03);
    }
  }

  img {
    max-width: 100%;
    border-radius: 5px;
  }
`;

interface ChatWindowProps {
  chatId: string;
  reportContext?: any;
  autoStartMessage?: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ chatId, reportContext, autoStartMessage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [chat, setChat] = useState<Chat | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [currentToolResults, setCurrentToolResults] = useState<ToolResult[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageFinalizedRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  useEffect(() => {
    fetchChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);
  
  useEffect(() => {
    scrollToBottom();
  }, [chat?.messages]);
  
  // Cleanup streaming on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // Auto-start effect: Send initial message (with or without report context)
  useEffect(() => {
    if (chat && autoStartMessage && !hasAutoStarted && !loading) {
      // Check if the autoStartMessage is already in the chat to avoid duplicates
      const messageAlreadyExists = chat.messages?.some(msg => 
        msg.role === MessageRole.USER && msg.content === autoStartMessage
      );
      
      if (messageAlreadyExists) {
        setHasAutoStarted(true);
        return;
      }
      setHasAutoStarted(true);
      
      // Send the auto-start message using proper streaming
      const handleAutoStart = async () => {
        try {
          setSending(true);
          setIsStreaming(true);
          setStreamingMessage('');
          setCurrentToolResults([]);
          messageFinalizedRef.current = false;
          
          
          // Create the context based on whether we have report context
          const streamingContext = reportContext ? {
            type: 'report_analysis',
            report_data: reportContext,
            user_request: autoStartMessage
          } : null;
          
          // Add user message to chat immediately
          const userMessage: Message = {
            id: 'auto-user-' + Date.now(),
            role: MessageRole.USER,
            content: autoStartMessage,
            timestamp: new Date().toISOString(),
          };
          
          setChat(prev => prev ? {
            ...prev,
            messages: [...prev.messages, userMessage]
          } : null);
          
          // Start streaming using sendStreamingMessage
          abortControllerRef.current = new AbortController();
          const response = await sendStreamingMessage(chatId, autoStartMessage, streamingContext);
          
          if (!response.ok) {
            throw new Error(`Streaming failed: ${response.status}`);
          }
          
          if (!response.body) {
            throw new Error('Response body is null');
          }
          
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let accumulatedContent = '';
          let currentToolResults: ToolResult[] = [];
          
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              const chunk = decoder.decode(value, { stream: true });
              const lines = chunk.split('\n');
              
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  const data = line.slice(6);
                  if (data === '[DONE]') continue;
                  
                  try {
                    const event = JSON.parse(data);
                    
                    switch (event.type) {
                      case 'status':
                        setStreamingMessage(event.message);
                        break;
                        
                      case 'tool_start':
                        if (event.tool_name) {
                          const newTool: ToolResult = {
                            id: `tool-${event.tool_name}-${Date.now()}`,
                            tool_name: event.tool_name,
                            status: 'running',
                            startTime: new Date(),
                            message: event.message,
                            arguments: event.data?.arguments
                          };
                          currentToolResults.push(newTool);
                          setCurrentToolResults([...currentToolResults]);
                        }
                        break;
                        
                      case 'tool_complete':
                        if (event.tool_name) {
                          currentToolResults = currentToolResults.map(tool => 
                            tool.tool_name === event.tool_name && tool.status === 'running'
                              ? {
                                  ...tool,
                                  status: 'completed',
                                  endTime: new Date(),
                                  message: event.message,
                                  result_preview: event.data?.result_preview,
                                  result: event.data?.result
                                }
                              : tool
                          );
                          setCurrentToolResults([...currentToolResults]);
                        }
                        break;
                        
                      case 'response_token':
                        accumulatedContent += event.data?.token || '';
                        setStreamingMessage(accumulatedContent);
                        break;
                        
                      case 'final_response':
                        accumulatedContent = event.data?.content || event.message || accumulatedContent;
                        setStreamingMessage(accumulatedContent);
                        break;
                    }
                  } catch (parseError) {
                    console.error('SSE JSON PARSE ERROR - Full JSON (length:', data.length, '):', data);
                    console.warn('Failed to parse SSE data:', data, parseError);
                  }
                }
              }
            }
          } finally {
            reader.releaseLock();
          }
          
          // Add assistant message to chat
          if (!messageFinalizedRef.current) {
            messageFinalizedRef.current = true;
            
            let finalContent = accumulatedContent;
            if (!finalContent || finalContent.trim().length === 0) {
              finalContent = "I'm sorry, something went wrong while processing your request. Please try again, and if the issue persists, contact support.";
            }
            
            const assistantMessage: Message = {
              id: 'auto-assistant-' + Date.now(),
              role: MessageRole.ASSISTANT,
              content: finalContent,
              timestamp: new Date().toISOString(),
              toolResults: currentToolResults.length > 0 ? currentToolResults : undefined
            };
            
            setChat(prev => prev ? {
              ...prev,
              messages: [...prev.messages, assistantMessage]
            } : null);
          }
          
        } catch (err: any) {
          console.error('Error in auto-start:', err);
          
          // Check if this is an access-related error
          const errorMessage = err.message || err.toString() || '';
          const isAccessError = isBlockingOrAccessMessage(errorMessage);
          
          if (isAccessError) {
            // If it's an access-related error during auto-start, display helpful message
            const helpfulMessage = `🛑 **Website Access Issue**\n\nThe website is blocking automated access during analysis. This is common with dealership sites and some listing platforms.\n\n**What you can do:**\n• Try searching for this vehicle on major sites like AutoTrader.com or Cars.com\n• Provide the vehicle details manually (year, make, model, price)\n• I can still help with vehicle research and analysis!\n\n**Just tell me the vehicle details and I'll provide comprehensive analysis.**`;
            
            // Add this as an assistant message
            if (chat && !messageFinalizedRef.current) {
              messageFinalizedRef.current = true;
              const assistantMessage: Message = {
                id: 'error-assistant-' + Date.now(),
                role: MessageRole.ASSISTANT,
                content: helpfulMessage,
                timestamp: new Date().toISOString()
              };
              
              setChat(prev => prev ? {
                ...prev,
                messages: [...prev.messages, assistantMessage]
              } : null);
            }
            
            setError('');
          } else {
            setError('Failed to auto-start chat. Please try sending a message manually.');
          }
        } finally {
          setSending(false);
          setIsStreaming(false);
          setStreamingMessage('');
          setCurrentToolResults([]);
        }
      };
      
      // Small delay to ensure UI is ready
      setTimeout(handleAutoStart, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat, reportContext, autoStartMessage, hasAutoStarted, chatId, loading]);
  
  const fetchChat = async () => {
    try {
      setLoading(true);
      const data = await getChat(chatId);
      setChat(data);
      setError('');

      // Auto-reconnect: if the last message is from the user (URL/VIN job) with no
      // assistant reply, the background job is still running — reconnect to resume.
      // Only reconnect for URL/VIN messages; simple queries have no job to reconnect to.
      const messages = data?.messages || [];
      const lastMessage = messages[messages.length - 1];
      const isLongJob = lastMessage?.role === MessageRole.USER &&
        (/https?:\/\/\S+/.test(lastMessage.content) || /[A-HJ-NPR-Z0-9]{17}/.test(lastMessage.content));
      if (isLongJob) {
        setTimeout(() => reconnectToStream(lastMessage.content), 300);
      }
    } catch (err: any) {
      console.error('Error fetching chat:', err);
      setError('Unable to load this chat. Please try again, and if the issue persists, contact support.');
    } finally {
      setLoading(false);
    }
  };

  const reconnectToStream = async (userMessage: string) => {
    try {
      setSending(true);
      setIsStreaming(true);
      setStreamingMessage('Reconnecting...');
      setCurrentToolResults([]);
      messageFinalizedRef.current = false;

      const response = await sendStreamingMessage(chatId, userMessage, null);

      if (!response.ok || !response.body) {
        throw new Error('Reconnect failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let currentToolResults: ToolResult[] = [];

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          for (const line of chunk.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            try {
              const event = JSON.parse(data);
              switch (event.type) {
                case 'status':
                  setStreamingMessage(event.message);
                  break;
                case 'tool_start':
                  if (event.tool_name) {
                    currentToolResults.push({
                      id: `tool-${event.tool_name}-${Date.now()}`,
                      tool_name: event.tool_name,
                      status: 'running',
                      startTime: new Date(),
                      message: event.message,
                      arguments: event.data?.arguments,
                    });
                    setCurrentToolResults([...currentToolResults]);
                  }
                  break;
                case 'tool_complete':
                  if (event.tool_name) {
                    currentToolResults = currentToolResults.map(tool =>
                      tool.tool_name === event.tool_name && tool.status === 'running'
                        ? { ...tool, status: 'completed', endTime: new Date(), message: event.message, result_preview: event.data?.result_preview, result: event.data?.result }
                        : tool
                    );
                    setCurrentToolResults([...currentToolResults]);
                  }
                  break;
                case 'final_response':
                  accumulatedContent = event.data?.content || event.message || accumulatedContent;
                  setStreamingMessage(accumulatedContent);
                  break;
              }
            } catch { /* ignore parse errors */ }
          }
        }
      } finally {
        reader.releaseLock();
      }

      if (!messageFinalizedRef.current && accumulatedContent) {
        messageFinalizedRef.current = true;
        const assistantMessage: Message = {
          id: 'reconnect-assistant-' + Date.now(),
          role: MessageRole.ASSISTANT,
          content: accumulatedContent,
          timestamp: new Date().toISOString(),
          toolResults: currentToolResults.length > 0 ? currentToolResults : undefined,
        };
        setChat(prev => prev ? { ...prev, messages: [...prev.messages, assistantMessage] } : null);
      }
    } catch (err) {
      console.error('Error reconnecting to stream:', err);
    } finally {
      setSending(false);
      setIsStreaming(false);
      setStreamingMessage('');
      setCurrentToolResults([]);
    }
  };
  
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() || sending || isStreaming) {
      return;
    }
    
    try {
      setSending(true);
      setIsStreaming(true);
      setStreamingMessage('');
      setCurrentToolResults([]);
      messageFinalizedRef.current = false;
      
      const messageContent = message;
      setMessage(''); // Clear input immediately
      
      // Create research context if needed
      let researchContext = null;
      
      // If we have report context, always include it
      if (reportContext) {
        researchContext = {
          type: 'report_analysis',
          report_data: reportContext,
          user_request: messageContent
        };
      } 
      // Otherwise, use the existing research context logic
      else if (chat?.type && ['deep_research', 'existing_car'].includes(chat.type)) {
        researchContext = {
          type: chat.type,
          requirements: chat.researchData?.requirements || {}
        };
      }
      
      // Add user message to chat immediately
      const userMessage: Message = {
        id: 'user-' + Date.now(),
        role: MessageRole.USER,
        content: messageContent,
        timestamp: new Date().toISOString(),
      };
      
      setChat(prev => prev ? {
        ...prev,
        messages: [...prev.messages, userMessage]
      } : null);
      
      // Start streaming using sendStreamingMessage
      abortControllerRef.current = new AbortController();
      const response = await sendStreamingMessage(chatId, messageContent, researchContext);
      
      if (!response.ok) {
        throw new Error(`Streaming failed: ${response.status}`);
      }
      
      if (!response.body) {
        throw new Error('Response body is null');
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let currentToolResults: ToolResult[] = [];
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') continue;
              
              try {
                const event = JSON.parse(data);
                
                switch (event.type) {
                  case 'status':
                    setStreamingMessage(event.message);
                    break;
                    
                  case 'tool_start':
                    if (event.tool_name) {
                      const newTool: ToolResult = {
                        id: `tool-${event.tool_name}-${Date.now()}`,
                        tool_name: event.tool_name,
                        status: 'running',
                        startTime: new Date(),
                        message: event.message,
                        arguments: event.data?.arguments
                      };
                      currentToolResults.push(newTool);
                      setCurrentToolResults([...currentToolResults]);
                    }
                    break;
                    
                  case 'tool_complete':
                    if (event.tool_name) {
                      currentToolResults = currentToolResults.map(tool => 
                        tool.tool_name === event.tool_name && tool.status === 'running'
                          ? {
                              ...tool,
                              status: 'completed',
                              endTime: new Date(),
                              message: event.message,
                              result_preview: event.data?.result_preview,
                              result: event.data?.result
                            }
                          : tool
                      );
                      setCurrentToolResults([...currentToolResults]);
                    }
                    break;
                    
                  case 'response_token':
                    accumulatedContent += event.data?.token || '';
                    setStreamingMessage(accumulatedContent);
                    break;
                    
                  case 'final_response':
                    accumulatedContent = event.data?.content || event.message || accumulatedContent;
                    setStreamingMessage(accumulatedContent);
                    break;
                }
              } catch (parseError) {
                console.error('SSE JSON PARSE ERROR - Full JSON (length:', data.length, '):', data);
                console.warn('Failed to parse SSE data:', data, parseError);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
      
      // Add assistant message to chat
      if (!messageFinalizedRef.current) {
        messageFinalizedRef.current = true;
        
        let finalContent = accumulatedContent;
        if (!finalContent || finalContent.trim().length === 0) {
          finalContent = "I'm sorry, something went wrong while processing your request. Please try again, and if the issue persists, contact support.";
        }
        
        const assistantMessage: Message = {
          id: 'assistant-' + Date.now(),
          role: MessageRole.ASSISTANT,
          content: finalContent,
          timestamp: new Date().toISOString(),
          toolResults: currentToolResults.length > 0 ? currentToolResults : undefined
        };
        
        setChat(prev => prev ? {
          ...prev,
          messages: [...prev.messages, assistantMessage]
        } : null);
      }
      
    } catch (err: any) {
      console.error('Error sending message:', err);
      
      // Check if this is an access-related error
      const errorMessage = err.message || err.toString() || '';
      const isAccessError = isBlockingOrAccessMessage(errorMessage);
      
      if (isAccessError) {
        const helpfulMessage = `🛑 **Website Access Issue**\n\nThe website is blocking automated access. This is common with dealership sites and some listing platforms.\n\n**What you can do:**\n• Try searching for this vehicle on major sites like AutoTrader.com or Cars.com\n• Provide the vehicle details manually (year, make, model, price)\n• I can still help with vehicle research and analysis!\n\n**Just tell me the vehicle details and I'll provide comprehensive analysis.**`;
        
        if (chat && !messageFinalizedRef.current) {
          messageFinalizedRef.current = true;
          const assistantMessage: Message = {
            id: 'error-assistant-' + Date.now(),
            role: MessageRole.ASSISTANT,
            content: helpfulMessage,
            timestamp: new Date().toISOString()
          };
          
          setChat(prev => prev ? {
            ...prev,
            messages: [...prev.messages, assistantMessage]
          } : null);
        }
        
        setError('');
      } else {
        setError('Something went wrong. Please try again, and if the issue persists, contact support.');
      }
    } finally {
      setSending(false);
      setIsStreaming(false);
      setStreamingMessage('');
      setCurrentToolResults([]);
      // Note: messageFinalizedRef is reset at the start of each new message
    }
  };
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const formatTimestamp = (timestamp: string) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };
  
  const getResearchProgress = () => {
    if (!chat?.researchData) return null;
    
    const { currentStep, totalSteps, status } = chat.researchData;
    const progress = (currentStep / totalSteps) * 100;
    
    return {
      progress,
      currentStep,
      totalSteps,
      isCompleted: status === ResearchStatus.COMPLETED
    };
  };

  const renderToolResults = (toolResults: ToolResult[]) => {
    if (!toolResults || toolResults.length === 0) return null;

    return (
      <Accordion sx={{ mt: 1, mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToolIcon color="primary" />
            <Typography variant="body2">
              Tool Usage ({toolResults.length} {toolResults.length === 1 ? 'tool' : 'tools'})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            {toolResults.map((tool) => (
              <ListItem key={tool.id} sx={{ px: 0 }}>
                <ListItemIcon>
                  {tool.status === 'completed' ? (
                    <CompletedIcon color="success" />
                  ) : tool.status === 'running' ? (
                    <CircularProgress size={20} />
                  ) : (
                    <StartIcon color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography variant="body2" fontWeight="bold" component="span">
                        {tool.tool_name.replace(/_/g, ' ').replace(/tool$/, '').trim()}
                      </Typography>
                      <Chip 
                        label={tool.status} 
                        size="small" 
                        color={tool.status === 'completed' ? 'success' : tool.status === 'running' ? 'warning' : 'default'}
                      />
                    </span>
                  }
                  secondary={
                    <span>
                      {tool.message && (
                        <Typography variant="caption" display="block" component="span">
                          {tool.message}
                        </Typography>
                      )}
                      {tool.result_preview && (
                        <Typography variant="caption" color="text.secondary" display="block" component="span">
                          {tool.result_preview}
                        </Typography>
                      )}
                      {tool.startTime && tool.endTime && (
                        <Typography variant="caption" color="text.secondary" display="block" component="span">
                          Duration: {Math.round((tool.endTime.getTime() - tool.startTime.getTime()) / 1000)}s
                        </Typography>
                      )}
                    </span>
                  }
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderStreamingToolResults = () => {
    if (!isStreaming || currentToolResults.length === 0) return null;

    return (
      <Paper 
        sx={{ 
          p: 2, 
          mb: 2, 
          backgroundColor: 'background.light',
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 2 
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ToolIcon color="primary" />
          <Typography variant="body2" fontWeight="bold">
            Processing Tools...
          </Typography>
        </Box>
        <List dense>
          {currentToolResults.map((tool) => (
            <ListItem key={tool.id} sx={{ px: 0 }}>
              <ListItemIcon>
                {tool.status === 'completed' ? (
                  <CompletedIcon color="success" />
                ) : (
                  <CircularProgress size={16} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {tool.tool_name.replace(/_/g, ' ').replace(/tool$/, '').trim()}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {tool.message}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    );
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  
  if (!chat) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Chat not found</Typography>
      </Box>
    );
  }
  
  const researchProgress = getResearchProgress();
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: isMobile ? 'calc(100vh - 64px)' : 'calc(100vh - 130px)',
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden'
    }}>
      {/* Chat header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: isMobile ? 1 : 2, 
          mb: isMobile ? 0.5 : 2, 
          borderRadius: isMobile ? 1 : 2, 
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">{chat.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Created {formatTimestamp(chat.createdAt)}
            </Typography>
          </Box>
          <Chip 
            label={chat.type === 'deep_research' ? 'Deep Research' : chat.type === 'existing_car' ? 'Car Analysis' : 'Chat'}
            color={chat.type === 'deep_research' ? 'info' : chat.type === 'existing_car' ? 'success' : 'primary'}
          />
        </Box>
        
        {/* Research progress bar */}
        {researchProgress && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {researchProgress.isCompleted 
                  ? 'Research completed' 
                  : `Step ${researchProgress.currentStep} of ${researchProgress.totalSteps}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(researchProgress.progress)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={researchProgress.progress} 
              color={researchProgress.isCompleted ? 'success' : 'primary'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}
      </Paper>
      
      {/* Messages area */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: isMobile ? 0.5 : 2, 
          mb: isMobile ? 0.5 : 2, 
          flexGrow: 1, 
          overflowY: 'auto',
          borderRadius: isMobile ? 1 : 2, 
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          minHeight: 0, // Important for flex-grow to work properly
          width: '100%',
          maxWidth: '100%'
        }}
      >
        {chat.messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">No messages yet. Start the conversation!</Typography>
          </Box>
 ) : (
          <>
            {chat.messages.map((msg, index) => (
              <Box 
                key={msg.id} 
                sx={{ 
                  mb: 3
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2">
                      {msg.role === 'assistant' ? 'Car Assistant' : 'You'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {formatTimestamp(msg.timestamp)}
                    </Typography>
                    {msg.isError && (
                      <Chip 
                        label="Error" 
                        size="small" 
                        color="error" 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                  
                  {/* Render tool results for assistant messages */}
                  {msg.role === 'assistant' && msg.toolResults && renderToolResults(msg.toolResults)}
                  
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      backgroundColor: msg.role === 'assistant' ? 'background.light' : 'primary.dark',
                      wordBreak: 'break-word'
                    }}
                  >
                    <MarkdownContainer>
                      <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                        {msg.content}
                      </ReactMarkdown>
                    </MarkdownContainer>
                    
                    {/* Render any metadata visualizations or interactive elements */}
                    {msg.metadata && msg.metadata.type === 'car_comparison' && (
                      <Paper sx={{ mt: 2, p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2">Car Comparison</Typography>
                        {/* Custom car comparison visualization would go here */}
                      </Paper>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
            
            {/* Show streaming tools and message */}
            {isStreaming && (
              <>
                {renderStreamingToolResults()}
                
                {/* Show current streaming message */}
                {streamingMessage && (
                  <Box 
                    sx={{ 
                      mb: 3
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2">Car Assistant</Typography>
                        <Chip 
                          label="Typing..." 
                          size="small" 
                          color="primary" 
                          sx={{ ml: 1 }}
                          icon={<CircularProgress size={12} />}
                        />
                      </Box>
                      <Box 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          backgroundColor: 'background.light',
                          wordBreak: 'break-word',
                          border: '1px solid',
                          borderColor: 'primary.main'
                        }}
                      >
                        <MarkdownContainer>
                          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                            {streamingMessage}
                          </ReactMarkdown>
                        </MarkdownContainer>
                      </Box>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </Paper>
      
      {/* Message input */}
      <Paper 
        component="form" 
        elevation={0}
        onSubmit={handleSendMessage}
        sx={{ 
          p: isMobile ? 1 : 2, 
          display: 'flex', 
          alignItems: 'center',
          borderRadius: isMobile ? 1 : 2, 
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          width: '100%',
          maxWidth: '100%'
        }}
      >
        <TextField
          fullWidth
          placeholder="Type your message..."
          variant="outlined"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={sending || isStreaming}
          multiline
          maxRows={isMobile ? 3 : 4}
          sx={{ mr: isMobile ? 1 : 2 }}
        />
        <IconButton 
          color="primary" 
          type="submit"
          disabled={sending || isStreaming || !message.trim()}
          sx={{ 
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
            }
          }}
        >
          <SendIcon />
        </IconButton>
      </Paper>
    </Box>
  );
};

export default ChatWindow;
